import React, { useState, useEffect } from 'react';
import api from '../services/api';
import CredencialCard from './CredencialCard';
import QRModal from './QRModal';
import PrintCredential from './PrintCredential';
import html2canvas from 'html2canvas';
import Swal from 'sweetalert2';

// Recibir props del Dashboard
const Credenciales = ({
  onOpenCreateModal,
  onOpenEditModal,
  refreshTrigger,
}) => {
  const [credenciales, setCredenciales] = useState([]);
  const [filteredCredenciales, setFilteredCredenciales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [departamentosList, setDepartamentosList] = useState([]);
  const [accesosList, setAccesosList] = useState([]);
  const [selectedCredentials, setSelectedCredentials] = useState(new Set());
  const [filters, setFilters] = useState({
    departamento: '',
    tipo_empleado: '',
    nombre_busqueda: '',
    acceso: '',
  });
  const [viewCardOpen, setViewCardOpen] = useState(false);
  const [viewingCredencial, setViewingCredencial] = useState(null);
  const [currentPageComplete, setCurrentPageComplete] = useState(0);
  const [currentPageIncomplete, setCurrentPageIncomplete] = useState(0);
  const itemsPerPage = 20;

  // Estados para QR y envío
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [selectedCredencialForQR, setSelectedCredencialForQR] = useState(null);
  const [sentStatus, setSentStatus] = useState({});

  // Colores para los badges
  const colors = {
    NARANJA: {
      normal: '#F68C2A',
      fuerte: '#E05A1A',
      claro: '#FBB036',
      gradiente: 'from-[#F68C2A] to-[#E05A1A]',
    },
    AZUL: {
      normal: '#064382',
      fuerte: '#002140',
      claro: '#0065A4',
      gradiente: 'from-[#064382] to-[#002140]',
    },
    AQUA: {
      normal: '#1E9FAB',
      fuerte: '#075F63',
      claro: '#22D1C2',
      gradiente: 'from-[#1E9FAB] to-[#075F63]',
    },
    CELESTE: {
      normal: '#57DFFF',
      fuerte: '#3076D1',
      claro: '#86EBFF',
      gradiente: 'from-[#57DFFF] to-[#3076D1]',
    },
    MORADO: {
      normal: '#766CAF',
      fuerte: '#3F3F92',
      claro: '#BBABD3',
      gradiente: 'from-[#766CAF] to-[#3F3F92]',
    },
    VERDE: {
      normal: '#30AE6E',
      fuerte: '#197338',
      claro: '#6CFFB4',
      gradiente: 'from-[#30AE6E] to-[#197338]',
    },
  };

  // Función para obtener colores (para impresión)
  const getColors = (colorName) => {
    const color = colors[colorName?.toUpperCase()];
    return color || colors.AZUL;
  };

  const isGuatemala = (credencial) => {
    if (!credencial) return false;
    const localidad = credencial.localidad || '';
    const acceso = credencial.acceso || '';
    return localidad === 'LOC_DHL_GUATEMALA' || acceso === 'Almacén Guatemala';
  };

  // Función para obtener el logo
  const getLogo = (credencial) => {
    if (isGuatemala(credencial)) {
      return '/media/DHL.png';
    }
    const razonSocial = credencial?.razon_social || '';
    if (
      razonSocial.includes('PACKSYS MEXICO') ||
      razonSocial.includes('PACKSYS DISTRIBUCION')
    ) {
      return '/media/PACKSYS.png';
    }
    if (razonSocial.includes('THAIBO')) return '/media/THAIBO.png';
    if (razonSocial.includes('REMBEX')) return '/media/REMBEX.png';
    if (razonSocial.includes('DENSEN')) return '/media/DENSEN.png';
    if (razonSocial.includes('INNOVA')) return '/media/INNOVA.png';
    if (razonSocial.includes('HALYCON')) return '/media/HALYCON.png';
    return '/media/PACKSYS.png';
  };

  // Función para verificar si es del grupo Packsys
  const isPacksysGroup = (credencial) => {
    const razonSocial = credencial?.razon_social || '';
    return (
      razonSocial.includes('PACKSYS MEXICO') ||
      razonSocial.includes('PACKSYS DISTRIBUCION') ||
      razonSocial.includes('HALYCON')
    );
  };

  // Cargar estados de envío desde localStorage
  useEffect(() => {
    const savedSentStatus = localStorage.getItem('credenciales_sent_status');
    if (savedSentStatus) {
      setSentStatus(JSON.parse(savedSentStatus));
    }
  }, []);

  // Función para confirmar envío
  const handleConfirmSent = (credencialId) => {
    const newSentStatus = {
      ...sentStatus,
      [credencialId]: true,
    };
    setSentStatus(newSentStatus);
    localStorage.setItem(
      'credenciales_sent_status',
      JSON.stringify(newSentStatus)
    );
  };

  // Función para imprimir múltiples credenciales
  const handlePrintMultipleCredentials = async () => {
    const selectedCreds = Array.from(selectedCredentials);
    const credsData = selectedCreds
      .map((id) => completeCredenciales.find((c) => c.id === id))
      .filter(Boolean);

    if (credsData.length === 0) {
      await Swal.fire({
        title: 'Selección requerida',
        text: 'Por favor, selecciona al menos una credencial para imprimir (máximo 4)',
        icon: 'warning',
        confirmButtonText: 'Aceptar',
      });
      return;
    }

    if (credsData.length > 4) {
      await Swal.fire({
        title: 'Límite excedido',
        text: 'Solo puedes imprimir un máximo de 4 credenciales por hoja',
        icon: 'error',
        confirmButtonText: 'Aceptar',
      });
      return;
    }

    // Verificar que todas las credenciales seleccionadas estén completas
    const incompleteCreds = credsData.filter((credencial) =>
      hasMissingInfo(credencial)
    );
    if (incompleteCreds.length > 0) {
      const names = incompleteCreds.map((c) => getNombreCompleto(c)).join('\n');
      await Swal.fire({
        title: 'Información pendiente',
        html: `Las siguientes credenciales tienen información incompleta:<br/><br/><strong>${names}</strong><br/><br/>Completa la información antes de imprimir.`,
        icon: 'error',
        confirmButtonText: 'Aceptar',
      });
      return;
    }

    // Preparar configuraciones de color para cada credencial
    const colorConfigs = credsData.map((credencial) =>
      getColors(credencial?.color)
    );

    await PrintCredential.printMultipleCredentials(
      credsData,
      colorConfigs,
      getLogo,
      isPacksysGroup,
      shouldShowRepse,
      getRepse,
      handleConfirmSent
    );

    // Limpiar selección después de imprimir
    setSelectedCredentials(new Set());
  };

  // Función para seleccionar/deseleccionar credencial
  const toggleSelectCredential = (credencial) => {
    // No permitir seleccionar si tiene info incompleta
    if (hasMissingInfo(credencial)) {
      Swal.fire({
        title: 'Información incompleta',
        text: `La credencial de ${getNombreCompleto(credencial)} tiene información pendiente. Complétala antes de seleccionar.`,
        icon: 'warning',
        confirmButtonText: 'Aceptar',
      });
      return;
    }

    const newSelected = new Set(selectedCredentials);
    if (newSelected.has(credencial.id)) {
      newSelected.delete(credencial.id);
    } else {
      if (newSelected.size < 4) {
        newSelected.add(credencial.id);
      } else {
        Swal.fire({
          title: 'Límite alcanzado',
          text: 'Solo puedes seleccionar un máximo de 4 credenciales para imprimir',
          icon: 'warning',
          confirmButtonText: 'Aceptar',
        });
        return;
      }
    }
    setSelectedCredentials(newSelected);
  };

  // Función para generar imagen del QR
  const handleGenerateQRImage = async (qrElement, credencial) => {
    try {
      const canvas = await html2canvas(qrElement, {
        scale: 3,
        backgroundColor: '#ffffff',
      });

      const link = document.createElement('a');
      link.download = `qr_${credencial.nombres}_${credencial.apellidos}.png`;
      link.href = canvas.toDataURL();
      link.click();
    } catch (error) {
      console.error('Error generating QR image:', error);
      await Swal.fire({
        title: 'Error',
        text: 'Error al generar la imagen del QR',
        icon: 'error',
        confirmButtonText: 'Cerrar',
      });
    }
  };

  // Obtener REPSE según razón social
  const getRepse = (credencial) => {
    const razonSocial = credencial?.razon_social || '';

    if (razonSocial.includes('THAIBO')) {
      return 'a1633bd0-518e-44b2-8f8c-2aa188ea8854';
    }
    if (razonSocial.includes('REMBEX')) {
      return 'd0f8da18-8938-4410-b9ee-de0e4ac1de09';
    }
    if (razonSocial.includes('DENSEN')) {
      return 'f2b879eb-b0de-4e8d-87a6-759fdf611d80';
    }
    if (razonSocial.includes('INNOVA')) {
      return 'ed545b30-e300-4bd3-b448-2be0621e33e4';
    }
    return null;
  };

  // Determinar si debe mostrar REPSE
  const shouldShowRepse = (credencial) => {
    const razonSocial = credencial?.razon_social || '';
    return (
      razonSocial.includes('THAIBO') ||
      razonSocial.includes('REMBEX') ||
      razonSocial.includes('DENSEN') ||
      razonSocial.includes('INNOVA')
    );
  };

  // Función para obtener el estilo del color
  const getColorStyle = (colorName) => {
    const color = colors[colorName?.toUpperCase()];
    if (!color) {
      return {
        backgroundColor: '#6B7280',
        borderColor: '#9CA3AF',
      };
    }
    return {
      backgroundColor: color.normal,
      borderColor: color.claro,
    };
  };

  // Función para verificar si una credencial tiene información pendiente
  const hasMissingInfo = (credencial) => {
    // Solo verificar los campos realmente necesarios para la impresión
    const requiredFields = [
      'nombres',
      'apellido_paterno',
      'acceso',
      'color',
      'tipo_empleado',
    ];

    // Estos campos son obligatorios SOLO si no son del grupo Packsys
    const externalRequiredFields = ['registro_patronal', 'nss'];

    // Verificar campos básicos
    const missingBasic = requiredFields.some((field) => {
      const value = credencial[field];
      return value === null || value === undefined || value === '';
    });

    // Si es del grupo Packsys, no validar registro_patronal ni nss
    const isPacksys = isPacksysGroup(credencial);

    let missingExternal = false;
    if (!isPacksys) {
      missingExternal = externalRequiredFields.some((field) => {
        const value = credencial[field];
        return value === null || value === undefined || value === '';
      });
    }

    // La foto no es obligatoria (tiene default)
    // oracle, razon_social, departamento, puesto, localidad no son obligatorios para impresión

    return missingBasic || missingExternal;
  };

  // Función para obtener los campos faltantes
  const getMissingFields = (credencial) => {
    const requiredFields = [
      'oracle',
      'razon_social',
      'registro_patronal',
      'departamento',
      'puesto',
      'localidad',
      'nss',
      'acceso',
      'color',
      'tipo_empleado',
      'foto_url',
    ];

    const fieldNames = {
      oracle: 'Oracle',
      razon_social: 'Razón Social',
      registro_patronal: 'Registro Patronal',
      departamento: 'Departamento',
      puesto: 'Puesto',
      localidad: 'Localidad',
      nss: 'NSS',
      acceso: 'Acceso',
      color: 'Color',
      tipo_empleado: 'Tipo de Empleado',
      foto_url: 'Foto',
    };

    return requiredFields
      .filter((field) => {
        const value = credencial[field];
        return value === null || value === undefined || value === '';
      })
      .map((field) => fieldNames[field]);
  };

  // Obtener lista de departamentos y accesos para los selects
  useEffect(() => {
    fetchFilterOptions();
  }, []);

  const fetchFilterOptions = async () => {
    try {
      const [departamentosRes, accesosRes] = await Promise.all([
        api.get('/opciones/departamentos'),
        api.get('/opciones/accesos'),
      ]);
      setDepartamentosList(departamentosRes.data);
      setAccesosList(accesosRes.data);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  const fetchCredenciales = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.departamento) params.departamento = filters.departamento;
      if (filters.tipo_empleado) params.tipo_empleado = filters.tipo_empleado;
      if (filters.acceso) params.acceso = filters.acceso;
      params.limit = 1000;

      const response = await api.get('/credenciales', { params });
      setCredenciales(response.data);
    } catch (error) {
      console.error('Error fetching credenciales:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCredenciales();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.departamento,
    filters.tipo_empleado,
    filters.acceso,
    refreshTrigger,
  ]);

  // Filtro de búsqueda por nombre (cliente-side)
  useEffect(() => {
    if (credenciales.length > 0) {
      let filtered = [...credenciales];

      // Ordenar por apellido paterno de A a Z
      filtered.sort((a, b) => {
        const apellidoA = (a.apellido_paterno || '').toLowerCase();
        const apellidoB = (b.apellido_paterno || '').toLowerCase();
        return apellidoA.localeCompare(apellidoB);
      });

      // Filtrar por nombre (búsqueda en nombres, apellido paterno, apellido materno)
      if (filters.nombre_busqueda && filters.nombre_busqueda.trim()) {
        const searchTerm = filters.nombre_busqueda.toLowerCase().trim();
        filtered = filtered.filter((credencial) => {
          const nombres = (credencial.nombres || '').toLowerCase();
          const apellidoPaterno = (
            credencial.apellido_paterno || ''
          ).toLowerCase();
          const apellidoMaterno = (
            credencial.apellido_materno || ''
          ).toLowerCase();
          const nombreCompleto = `${nombres} ${apellidoPaterno} ${apellidoMaterno}`;

          return (
            nombres.includes(searchTerm) ||
            apellidoPaterno.includes(searchTerm) ||
            apellidoMaterno.includes(searchTerm) ||
            nombreCompleto.includes(searchTerm)
          );
        });
      }

      setFilteredCredenciales(filtered);
      setCurrentPageComplete(0);
      setCurrentPageIncomplete(0);
    } else {
      setFilteredCredenciales([]);
    }
  }, [credenciales, filters.nombre_busqueda]);

  const handleViewCard = (credencial) => {
    setViewingCredencial(credencial);
    setViewCardOpen(true);
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: '¿Estás seguro?',
      text: 'Esta acción no se puede deshacer',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
    });

    if (result.isConfirmed) {
      try {
        await api.delete(`/credenciales/${id}`);
        fetchCredenciales();
        await Swal.fire({
          title: 'Eliminada',
          text: 'La credencial ha sido eliminada',
          icon: 'success',
          timer: 2000,
          timerProgressBar: true,
        });
      } catch (error) {
        console.error('Error deleting credencial:', error);
        await Swal.fire({
          title: 'Error',
          text: 'Error al eliminar la credencial',
          icon: 'error',
          confirmButtonText: 'Cerrar',
        });
      }
    }
  };

  const handleEdit = (credencial) => {
    onOpenEditModal(credencial);
  };

  const handleCreate = () => {
    onOpenCreateModal();
  };

  const resetFilters = () => {
    setFilters({
      departamento: '',
      tipo_empleado: '',
      nombre_busqueda: '',
      acceso: '',
    });
  };

  // Separar credenciales completas e incompletas
  const incompleteCredenciales = filteredCredenciales.filter(hasMissingInfo);
  const completeCredenciales = filteredCredenciales.filter(
    (c) => !hasMissingInfo(c)
  );

  // Paginación para completas
  const paginatedCompleteCredenciales = completeCredenciales.slice(
    currentPageComplete * itemsPerPage,
    (currentPageComplete + 1) * itemsPerPage
  );
  const totalPagesComplete = Math.ceil(
    completeCredenciales.length / itemsPerPage
  );

  // Paginación para incompletas
  const paginatedIncompleteCredenciales = incompleteCredenciales.slice(
    currentPageIncomplete * itemsPerPage,
    (currentPageIncomplete + 1) * itemsPerPage
  );
  const totalPagesIncomplete = Math.ceil(
    incompleteCredenciales.length / itemsPerPage
  );

  // Función para obtener el nombre completo formateado
  const getNombreCompleto = (credencial) => {
    const partes = [
      credencial.apellido_paterno,
      credencial.apellido_materno,
      credencial.nombres,
    ].filter(Boolean);
    return partes.join(' ') || 'Sin nombre';
  };

  // Componente de paginación reutilizable
  const Pagination = ({
    currentPage,
    totalPages,
    onPageChange,
    totalItems,
  }) => (
    <div className="flex justify-between items-center mt-6">
      <button
        onClick={() => onPageChange(Math.max(0, currentPage - 1))}
        disabled={currentPage === 0}
        className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed border border-white/20"
      >
        Anterior
      </button>
      <span className="text-sm text-blue-200">
        Página {currentPage + 1} de {totalPages} ({totalItems} registros)
      </span>
      <button
        onClick={() => onPageChange(Math.min(totalPages - 1, currentPage + 1))}
        disabled={currentPage === totalPages - 1}
        className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed border border-white/20"
      >
        Siguiente
      </button>
    </div>
  );

  // Componente para el círculo de color
  const ColorCircle = ({ colorName }) => {
    const colorStyle = getColorStyle(colorName);
    return (
      <div className="flex items-center gap-2">
        <div
          className="w-8 h-8 rounded-full shadow-lg transition-all duration-300 hover:scale-110"
          style={{
            backgroundColor: colorStyle.backgroundColor,
            boxShadow: `0 0 10px ${colorStyle.backgroundColor}`,
            border: `2px solid ${colorStyle.borderColor}`,
          }}
        />
      </div>
    );
  };

  // Renderizado de tabla de credenciales
  const renderCredencialesTable = (
    credencialesList,
    showAlertColumn = false
  ) => (
    <div className="bg-white/10 backdrop-blur-xl rounded-2xl shadow-xl overflow-hidden border border-white/20">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-white/20">
          <thead className="bg-white/5">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-blue-200 uppercase tracking-wider">
                ✅?
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-blue-200 uppercase tracking-wider">
                Nombre Completo
              </th>
              {!showAlertColumn && (
                <>
                  <th className="px-6 py-3 text-left text-xs font-medium text-blue-200 uppercase tracking-wider">
                    Tipo
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-blue-200 uppercase tracking-wider">
                    Color
                  </th>
                </>
              )}
              {showAlertColumn && (
                <>
                  <th className="px-6 py-3 text-left text-xs font-medium text-orange-200 uppercase tracking-wider">
                    Alerta
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-blue-200 uppercase tracking-wider">
                    Tipo
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-blue-200 uppercase tracking-wider">
                    Color
                  </th>
                </>
              )}
              <th className="px-6 py-3 text-right text-xs font-medium text-blue-200 uppercase tracking-wider">
                Acciones
              </th>
              {!showAlertColumn && (
                <>
                  <th className="px-6 py-3 text-left text-xs font-medium text-blue-200 uppercase tracking-wider">
                    Compartir
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-blue-200 uppercase tracking-wider">
                    ¿Envió?
                  </th>
                </>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-white/10">
            {credencialesList.length === 0 ? (
              <tr>
                <td
                  colSpan={showAlertColumn ? 5 : 9}
                  className="px-6 py-12 text-center"
                >
                  <div className="text-white/60">
                    No se encontraron credenciales
                  </div>
                </td>
              </tr>
            ) : (
              credencialesList.map((credencial) => (
                <tr
                  key={credencial.id}
                  className="hover:bg-white/5 transition-colors"
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={selectedCredentials.has(credencial.id)}
                      onChange={() => toggleSelectCredential(credencial)}
                      disabled={hasMissingInfo(credencial)}
                      className="form-checkbox h-5 w-5 text-blue-600 rounded border-white/30 bg-white/20 focus:ring-blue-500 focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                      style={{
                        accentColor: '#3b82f6',
                        backgroundColor: selectedCredentials.has(credencial.id)
                          ? '#3b82f6'
                          : 'rgba(255,255,255,0.2)',
                      }}
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-white">
                      {getNombreCompleto(credencial)}
                    </div>
                    <div className="text-xs text-blue-300/70 mt-1">
                      {credencial.nombres} {credencial.apellido_paterno}{' '}
                      {credencial.apellido_materno}
                    </div>
                  </td>
                  {!showAlertColumn && (
                    <>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            credencial.tipo_empleado === 'ADMINISTRATIVO'
                              ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30'
                              : credencial.tipo_empleado === 'OPERATIVO'
                                ? 'bg-orange-500/20 text-orange-300 border border-orange-500/30'
                                : 'bg-gray-500/20 text-gray-300 border border-gray-500/30'
                          }`}
                        >
                          {credencial.tipo_empleado || '-'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <ColorCircle colorName={credencial.color} />
                      </td>
                    </>
                  )}
                  {showAlertColumn && (
                    <>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1">
                          {getMissingFields(credencial).map((field, idx) => (
                            <span
                              key={idx}
                              className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-500/20 text-yellow-300 border border-yellow-500/30"
                            >
                              ⚠️ Falta: {field}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            credencial.tipo_empleado === 'ADMINISTRATIVO'
                              ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30'
                              : credencial.tipo_empleado === 'OPERATIVO'
                                ? 'bg-orange-500/20 text-orange-300 border border-orange-500/30'
                                : 'bg-gray-500/20 text-gray-300 border border-gray-500/30'
                          }`}
                        >
                          {credencial.tipo_empleado || '-'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <ColorCircle colorName={credencial.color} />
                      </td>
                    </>
                  )}
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleViewCard(credencial)}
                      className="text-green-400 hover:text-green-300 mr-3 transition-colors"
                      title="Ver credencial"
                    >
                      Ver
                    </button>
                    <button
                      onClick={() => handleEdit(credencial)}
                      className="text-blue-400 hover:text-blue-300 mr-3 transition-colors"
                      title="Editar credencial"
                    >
                      Editar
                    </button>
                    <button
                      onClick={() => handleDelete(credencial.id)}
                      className="text-red-400 hover:text-red-300 transition-colors"
                      title="Eliminar credencial"
                    >
                      Eliminar
                    </button>
                  </td>
                  {!showAlertColumn && (
                    <>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              if (credencial && credencial.id) {
                                setSelectedCredencialForQR(credencial);
                                setQrModalOpen(true);
                              }
                            }}
                            className="bg-green-500 hover:bg-green-600 text-white px-3 py-1 rounded-lg text-xs transition-colors"
                            title="Generar QR"
                          >
                            QR
                          </button>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center justify-center">
                          <input
                            type="checkbox"
                            checked={sentStatus[credencial.id] || false}
                            disabled
                            className="w-5 h-5 rounded border-white/30 bg-white/20 checked:bg-green-500 disabled:opacity-100 cursor-default"
                            style={{
                              accentColor: sentStatus[credencial.id]
                                ? '#22c55e'
                                : '#6b7280',
                            }}
                          />
                        </div>
                      </td>
                    </>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="animate-fadeInUp">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-white">
          Gestión de Credenciales
        </h2>
        <div className="flex gap-3">
          <button
            onClick={handlePrintMultipleCredentials}
            className={`bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02] px-4 py-2 flex items-center space-x-2 ${selectedCredentials.size === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
            disabled={selectedCredentials.size === 0}
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"
              />
            </svg>
            <span>Imprimir ({selectedCredentials.size})</span>
          </button>
          <button
            onClick={handleCreate}
            className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02] px-4 py-2 flex items-center space-x-2"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 4v16m8-8H4"
              />
            </svg>
            <span>Nueva Credencial</span>
          </button>
        </div>
      </div>

      {/* Mostrar contador de selección */}
      {selectedCredentials.size > 0 && (
        <div className="mb-4 p-3 bg-blue-500/20 rounded-xl border border-blue-500/30">
          <p className="text-blue-200 text-sm">
            {selectedCredentials.size} credencial(es) seleccionada(s). Máximo 4
            por impresión.
          </p>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white/10 backdrop-blur-xl rounded-2xl shadow-xl p-6 mb-6 border border-white/20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <input
            type="text"
            placeholder="Buscar por nombre, apellido paterno o materno..."
            value={filters.nombre_busqueda}
            onChange={(e) =>
              setFilters({ ...filters, nombre_busqueda: e.target.value })
            }
            className="w-full px-4 py-2 bg-white/20 border border-white/30 rounded-xl text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-transparent transition-all duration-300"
          />
          <select
            value={filters.departamento}
            onChange={(e) =>
              setFilters({ ...filters, departamento: e.target.value })
            }
            className="w-full px-4 py-2 bg-white/20 border border-white/30 rounded-xl text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-transparent transition-all duration-300"
          >
            <option value="" className="bg-gray-800">
              Todos los departamentos
            </option>
            {departamentosList.map((dept, idx) => (
              <option key={idx} value={dept} className="bg-gray-800">
                {dept}
              </option>
            ))}
          </select>
          <select
            value={filters.tipo_empleado}
            onChange={(e) =>
              setFilters({ ...filters, tipo_empleado: e.target.value })
            }
            className="w-full px-4 py-2 bg-white/20 border border-white/30 rounded-xl text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-transparent transition-all duration-300"
          >
            <option value="" className="bg-gray-800">
              Todos los tipos
            </option>
            <option value="ADMINISTRATIVO" className="bg-gray-800">
              Administrativo
            </option>
            <option value="OPERATIVO" className="bg-gray-800">
              Operativo
            </option>
          </select>
          <select
            value={filters.acceso}
            onChange={(e) => setFilters({ ...filters, acceso: e.target.value })}
            className="w-full px-4 py-2 bg-white/20 border border-white/30 rounded-xl text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-transparent transition-all duration-300"
          >
            <option value="" className="bg-gray-800">
              Todos los accesos
            </option>
            {accesosList.map((acceso, idx) => (
              <option key={idx} value={acceso} className="bg-gray-800">
                {acceso}
              </option>
            ))}
          </select>
          <div className="flex space-x-2">
            <button
              onClick={resetFilters}
              className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02] w-full px-4 py-2 space-x-2"
            >
              Limpiar Filtros
            </button>
          </div>
        </div>

        {/* Indicador de resultados de búsqueda */}
        {filters.nombre_busqueda && (
          <div className="mt-3 text-sm text-blue-300">
            Mostrando {filteredCredenciales.length} resultado(s)
            {filters.nombre_busqueda &&
              ` para nombre "${filters.nombre_busqueda}"`}
          </div>
        )}
      </div>

      {/* Credenciales con información pendiente */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400"></div>
        </div>
      ) : (
        <>
          {incompleteCredenciales.length > 0 && (
            <>
              <div className="mb-4">
                <h3 className="text-xl font-bold text-yellow-400 flex items-center gap-2">
                  <span className="text-yellow-400">⚠️</span>
                  Información Pendiente
                  <span className="text-sm font-normal text-blue-300 bg-white/10 px-2 py-1 rounded-full ml-2">
                    {incompleteCredenciales.length} credencial(es)
                  </span>
                </h3>
                <p className="text-sm text-blue-300/70 mt-1">
                  Estas credenciales requieren completar información faltante
                </p>
              </div>
              {renderCredencialesTable(paginatedIncompleteCredenciales, true)}
              {totalPagesIncomplete > 1 && (
                <Pagination
                  currentPage={currentPageIncomplete}
                  totalPages={totalPagesIncomplete}
                  onPageChange={setCurrentPageIncomplete}
                  totalItems={incompleteCredenciales.length}
                />
              )}
              <div className="my-8 border-t border-white/20"></div>
            </>
          )}

          {/* Credenciales completas */}
          <div className="mb-4">
            <h3 className="text-xl font-bold text-white">
              Credenciales Completas
              <span className="text-sm font-normal text-blue-300 bg-white/10 px-2 py-1 rounded-full ml-2">
                {completeCredenciales.length} credencial(es)
              </span>
            </h3>
          </div>
          {renderCredencialesTable(paginatedCompleteCredenciales, false)}

          {/* Pagination - Solo para credenciales completas */}
          {totalPagesComplete > 1 && (
            <Pagination
              currentPage={currentPageComplete}
              totalPages={totalPagesComplete}
              onPageChange={setCurrentPageComplete}
              totalItems={completeCredenciales.length}
            />
          )}
        </>
      )}

      {/* CredencialCard */}
      <CredencialCard
        isOpen={viewCardOpen}
        onClose={() => setViewCardOpen(false)}
        credencial={viewingCredencial}
      />

      {/* QR Modal (sin botón de validar envío) */}
      <QRModal
        isOpen={qrModalOpen}
        onClose={() => {
          setQrModalOpen(false);
          setSelectedCredencialForQR(null);
        }}
        credencial={selectedCredencialForQR}
        onGenerateImage={handleGenerateQRImage}
      />
    </div>
  );
};

export default Credenciales;
